/* ============================================================
   AQUA Product Page — interactions
   ============================================================
   Custom elements:
     <aqua-gallery>          gallery + dots + photo/video toggle
     <aqua-variant-picker>   country + bundle selection
     <aqua-accessory>        accessory card variant + qty
     <aqua-buy-bar>          sticky bottom bar + cart submit
   ============================================================ */

(function () {
  'use strict';

  // ----- shared utilities -----

  function formatMoney(cents, currency) {
    if (typeof cents !== 'number') cents = parseInt(cents, 10) || 0;
    var amount = (cents / 100).toFixed(2).replace(/\.00$/, '');
    var parts = amount.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    var iso = (window.Shopify && Shopify.currency && Shopify.currency.active) || currency || '';
    return (iso ? iso + ' ' : '') + parts.join('.');
  }

  function findVariant(variants, optionMap) {
    return variants.find(function (v) {
      return Object.keys(optionMap).every(function (pos) {
        var idx = parseInt(pos, 10) - 1;
        return v.options[idx] === optionMap[pos];
      });
    });
  }

  // ============================================================
  // <aqua-gallery>
  // ============================================================

  class AquaGallery extends HTMLElement {
    constructor() {
      super();
      this._index = 0;
    }

    connectedCallback() {
      this._slides = Array.from(this.querySelectorAll('[data-slide]'));
      this._dots = Array.from(this.querySelectorAll('[data-dot]'));
      this._navPrev = this.querySelector('[data-nav="prev"]');
      this._navNext = this.querySelector('[data-nav="next"]');
      this._toggleBtns = Array.from(this.querySelectorAll('[data-toggle]'));
      this._panels = Array.from(this.querySelectorAll('[data-panel]'));

      if (this._navPrev) this._navPrev.addEventListener('click', () => this.prev());
      if (this._navNext) this._navNext.addEventListener('click', () => this.next());

      this._dots.forEach((dot, i) => {
        dot.addEventListener('click', () => this.go(i));
      });

      this._toggleBtns.forEach((btn) => {
        btn.addEventListener('click', () => {
          this.switchPanel(btn.dataset.toggle);
        });
      });

      // Pause videos when leaving panel
      this._lastPanel = 'photo';
    }

    go(i) {
      if (this._slides.length === 0) return;
      this._index = ((i % this._slides.length) + this._slides.length) % this._slides.length;
      this._slides.forEach((s, idx) => s.classList.toggle('is-active', idx === this._index));
      this._dots.forEach((d, idx) => d.classList.toggle('is-active', idx === this._index));
    }

    _isHidden(slide) {
      return !!(slide && slide.classList.contains('is-bundle-hidden'));
    }

    next() {
      var n = this._slides.length;
      if (!n) return;
      var i = this._index;
      for (var step = 0; step < n; step++) {
        i = (i + 1) % n;
        if (!this._isHidden(this._slides[i])) { this.go(i); return; }
      }
    }

    prev() {
      var n = this._slides.length;
      if (!n) return;
      var i = this._index;
      for (var step = 0; step < n; step++) {
        i = (i - 1 + n) % n;
        if (!this._isHidden(this._slides[i])) { this.go(i); return; }
      }
    }

    /**
     * Hide slides whose bundle tag belongs to *another* bundle.
     * activeTag = current bundle's tag; allTags = all known bundle tags.
     * Slides with empty tags or tags not in allTags stay visible (treated as generic).
     * If jumpToMatch is true, also advance to the first slide whose tag === activeTag
     * (so clicking a new bundle lands on that bundle's hero shot).
     */
    filterByBundle(activeTag, allTags, jumpToMatch) {
      activeTag = (activeTag || '').toLowerCase();
      var others = (allTags || []).map(function(t){ return (t || '').toLowerCase(); })
                                  .filter(function(t){ return t && t !== activeTag; });
      this._slides.forEach((s, i) => {
        var tag = (s.dataset.bundleTag || '').toLowerCase();
        var hide = !!tag && others.indexOf(tag) !== -1;
        s.classList.toggle('is-bundle-hidden', hide);
        if (this._dots[i]) this._dots[i].classList.toggle('is-bundle-hidden', hide);
      });

      if (jumpToMatch) {
        // Jump to the first slide that matches this bundle exactly.
        var firstMatch = this._slides.findIndex((s) => {
          return (s.dataset.bundleTag || '').toLowerCase() === activeTag;
        });
        if (firstMatch >= 0) { this.go(firstMatch); return; }
      }

      // Fallback: if the current slide just got hidden, jump to the first visible one.
      if (this._isHidden(this._slides[this._index])) {
        var firstVisible = this._slides.findIndex((s) => !this._isHidden(s));
        if (firstVisible >= 0) this.go(firstVisible);
      }
    }

    // ── FIX 1 ──────────────────────────────────────────────────
    // Jump to the slide that matches the variant's featured image.
    // Checks (in priority order):
    //   1. data-media-id  matching featured_image.id   (Dawn / Online Store 2.0)
    //   2. data-image-id  matching featured_image.id   (older themes)
    //   3. An <img> whose src contains the image filename
    // Falls back gracefully if no match is found.
    goToVariantImage(variant) {
      if (!variant || !variant.featured_image) return;

      var mediaId  = String(variant.featured_image.id  || '');
      var position = variant.featured_image.position;

      // 1 — match by data-media-id / data-image-id
      var byId = this._slides.findIndex((s) => {
        return (
          s.dataset.mediaId  === mediaId ||
          s.dataset.imageId  === mediaId
        );
      });
      if (byId >= 0 && !this._isHidden(this._slides[byId])) {
        this.go(byId);
        return;
      }

      // 2 — match by image filename inside src (works when media IDs aren't
      //     written to HTML but the image file name is predictable)
      var src = variant.featured_image.src || '';
      // Extract just the filename without extension and query string
      var fileMatch = src.match(/\/([^/?#]+)\.[a-z]+(\?|$)/i);
      var filename = fileMatch ? fileMatch[1] : '';
      if (filename) {
        var bySrc = this._slides.findIndex((s) => {
          var img = s.querySelector('img');
          return img && img.src && img.src.indexOf(filename) !== -1;
        });
        if (bySrc >= 0 && !this._isHidden(this._slides[bySrc])) {
          this.go(bySrc);
          return;
        }
      }

      // 3 — fall back to position (1-based index from Shopify media list)
      if (position && position > 0) {
        var posIdx = position - 1;
        if (posIdx < this._slides.length && !this._isHidden(this._slides[posIdx])) {
          this.go(posIdx);
        }
      }
    }
    // ── END FIX 1 ──────────────────────────────────────────────

    switchPanel(name) {
      if (name === this._lastPanel) return;
      this._panels.forEach((p) => {
        p.classList.toggle('is-active', p.dataset.panel === name);
      });
      this._toggleBtns.forEach((b) => {
        var on = b.dataset.toggle === name;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      // Pause videos when switching away
      if (name !== 'video') {
        this.querySelectorAll('video').forEach((v) => {
          try { v.pause(); v.currentTime = 0; } catch (_) {}
        });
      }
      this._lastPanel = name;
    }
  }

  if (!customElements.get('aqua-gallery')) {
    customElements.define('aqua-gallery', AquaGallery);
  }

  // ============================================================
  // <aqua-variant-picker>
  // ============================================================

  class AquaVariantPicker extends HTMLElement {
    connectedCallback() {
      var dataEl = this.querySelector('[data-aqua-variants]');
      try {
        this._variants = JSON.parse(dataEl ? dataEl.textContent : '[]');
      } catch (e) {
        this._variants = [];
      }

      this._variantInput = this.querySelector('[data-variant-input]');

      this._countryInputs = Array.from(this.querySelectorAll('input[name^="aqua-country"]'));
      this._bundleInputs = Array.from(this.querySelectorAll('input[name^="aqua-bundle"]'));

      this._countryInputs.forEach((input) => {
        input.addEventListener('change', () => this.handleCountry(input));
      });

      this._bundleInputs.forEach((input) => {
        input.addEventListener('change', () => this.handleBundle(input));
      });

      // Apply gallery filter on initial render to match the pre-selected bundle.
      this.filterGallery();
    }

    /** Normalize a bundle name into the alt-tag format the user uses on images.
     *  e.g. "Standard Bundle" -> "Standard", "Fly More Bundle" -> "FlyMore",
     *  "Land and Sea Bundle" -> "LandandSea". Comparison is case-insensitive
     *  (see filterByBundle), so casing variations work too.
     */
    _bundleNameToTag(name) {
      return String(name || '')
        .replace(/\s+/g, '')
        .replace(/Bundle$/i, '');
    }

    /** Get all known bundle tags from the cards in this picker. */
    _allBundleTags() {
      var cards = Array.from(this.querySelectorAll('.aqua-bundle-card'));
      var tags = cards.map((c) => this._bundleNameToTag(c.dataset.bundleName || ''));
      return tags.filter(function(t){ return !!t; });
    }

    /** Find the gallery scoped to the same section and ask it to filter.
     *  jumpToMatch=true → also advance to that bundle's hero image (used on click).
     */
    filterGallery(jumpToMatch) {
      var sectionId = this.getAttribute('data-section-id') || (this.closest('[data-section-id]') && this.closest('[data-section-id]').dataset.sectionId);
      var gallery = sectionId
        ? document.querySelector('aqua-gallery[data-section-id="' + sectionId + '"]')
        : document.querySelector('aqua-gallery');
      if (!gallery || typeof gallery.filterByBundle !== 'function') return;

      var activeCard = this.querySelector('.aqua-bundle-card.is-active') || this.querySelector('.aqua-bundle-card');
      var activeTag = activeCard ? this._bundleNameToTag(activeCard.dataset.bundleName || '') : '';
      gallery.filterByBundle(activeTag, this._allBundleTags(), !!jumpToMatch);
    }

    // ── FIX 2 ──────────────────────────────────────────────────
    // Helper: find the gallery element for this section.
    _getGallery() {
      var sectionId = this.getAttribute('data-section-id') ||
        (this.closest('[data-section-id]') && this.closest('[data-section-id]').dataset.sectionId);
      return sectionId
        ? document.querySelector('aqua-gallery[data-section-id="' + sectionId + '"]')
        : document.querySelector('aqua-gallery');
    }
    // ── END FIX 2 ──────────────────────────────────────────────

    currentSelections() {
      var map = {};
      var country = this._countryInputs.find((i) => i.checked);
      var bundle = this._bundleInputs.find((i) => i.checked);
      if (country) map[country.dataset.optionPosition] = country.value;
      if (bundle) map[bundle.dataset.optionPosition] = bundle.value;
      return map;
    }

    handleCountry(input) {
      // Visual states
      input.closest('.aqua-country-list').querySelectorAll('.aqua-country').forEach((el) => {
        el.classList.toggle('is-active', el.contains(input.checked ? input : null));
      });
      // Re-find variant for each visible bundle for the new country and update its card
      this.refreshBundleCards();
      this.updateMainVariant();
    }

    handleBundle(input) {
      var card = input.closest('.aqua-bundle-card');
      if (!card) return;
      this.querySelectorAll('.aqua-bundle-card').forEach((c) => {
        c.classList.toggle('is-active', c === card);
      });
      this.updateMainVariant();
      this.filterGallery(true); // jump to the new bundle's hero image (data-bundle-tag path)
    }

    refreshBundleCards() {
      var country = this._countryInputs.find((i) => i.checked);
      if (!country) return;
      var countryPos = parseInt(country.dataset.optionPosition, 10);

      this.querySelectorAll('.aqua-bundle-card').forEach((card) => {
        var bundleInput = card.querySelector('input[type="radio"]');
        if (!bundleInput) return;
        var bundlePos = parseInt(bundleInput.dataset.optionPosition, 10);
        var optionMap = {};
        optionMap[countryPos] = country.value;
        optionMap[bundlePos] = bundleInput.value;
        var v = findVariant(this._variants, optionMap);
        if (!v) return;

        card.dataset.variantId = v.id;
        var priceEl = card.querySelector('.aqua-bundle-card__price');
        var compareEl = card.querySelector('.aqua-bundle-card__compare');
        var discEl = card.querySelector('.aqua-bundle-card__discount');
        if (priceEl) priceEl.textContent = formatMoney(v.price);
        if (v.compare_at_price && v.compare_at_price > v.price) {
          if (compareEl) {
            compareEl.textContent = formatMoney(v.compare_at_price);
            compareEl.classList.remove('aqua-hidden');
          }
          if (discEl) {
            var pct = Math.round(((v.compare_at_price - v.price) * 100) / v.compare_at_price);
            discEl.textContent = '- ' + pct + '%';
            discEl.classList.remove('aqua-hidden');
          }
        } else {
          if (compareEl) { compareEl.textContent = ''; compareEl.classList.add('aqua-hidden'); }
          if (discEl) { discEl.textContent = ''; discEl.classList.add('aqua-hidden'); }
        }
      });
    }

    updateMainVariant() {
      var sel = this.currentSelections();
      var v = findVariant(this._variants, sel);
      if (!v) return;
      this._currentVariant = v;
      this.dataset.currentVariant = v.id;
      if (this._variantInput) this._variantInput.value = v.id;

      // Update buy bar price
      var bar = document.querySelector('aqua-buy-bar');
      if (bar) bar.updatePrice && bar.updatePrice(v);

      // ── FIX 3 ──────────────────────────────────────────────
      // Switch the gallery to the image that belongs to this variant.
      // This runs on EVERY variant change (country + bundle), so the
      // gallery always reflects whichever variant is currently selected.
      var gallery = this._getGallery();
      if (gallery && typeof gallery.goToVariantImage === 'function') {
        gallery.goToVariantImage(v);
      }
      // ── END FIX 3 ──────────────────────────────────────────

      // Update URL with variant id (for shareability)
      try {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', v.id);
        window.history.replaceState({}, '', url.toString());
      } catch (_) {}
    }

    getCurrentVariantId() {
      return (this._variantInput && this._variantInput.value) || this.dataset.currentVariant;
    }
  }

  if (!customElements.get('aqua-variant-picker')) {
    customElements.define('aqua-variant-picker', AquaVariantPicker);
  }

  // ============================================================
  // <aqua-accessory>
  // ============================================================

  class AquaAccessory extends HTMLElement {
    connectedCallback() {
      var dataEl = this.querySelector('[data-aqua-accessory-variants]');
      try {
        this._variants = JSON.parse(dataEl ? dataEl.textContent : '[]');
      } catch (e) {
        this._variants = [];
      }

      this._selects = Array.from(this.querySelectorAll('.aqua-accessory-card__select'));
      this._qtyInput = this.querySelector('[data-acc-qty]');
      this._qtyButtons = Array.from(this.querySelectorAll('.aqua-accessory-card__qty-btn'));
      this._checkbox = this.querySelector('[data-acc-checkbox]');
      this._priceEl = this.querySelector('[data-acc-price]');
      this._compareEl = this.querySelector('[data-acc-compare]');
      this._discEl = this.querySelector('[data-acc-discount]');

      this._selects.forEach((s) => s.addEventListener('change', () => this.updateVariant()));

      this._qtyButtons.forEach((btn) => {
        btn.addEventListener('click', () => {
          var val = parseInt(this._qtyInput.value, 10) || 1;
          val = btn.dataset.qty === '+' ? val + 1 : Math.max(1, val - 1);
          this._qtyInput.value = val;
        });
      });

      // Sync country selector if accessory has a "country" option
      var picker = document.querySelector('aqua-variant-picker');
      if (picker) {
        document.addEventListener('aqua:country-changed', (e) => {
          var country = e.detail && e.detail.country;
          if (!country) return;
          this._selects.forEach((s) => {
            for (var i = 0; i < s.options.length; i++) {
              if (s.options[i].value.toLowerCase() === country.toLowerCase()) {
                s.value = s.options[i].value;
                this.updateVariant();
                break;
              }
            }
          });
        });
      }
    }

    updateVariant() {
      if (!this._variants.length) return;
      var optionMap = {};
      this._selects.forEach((s) => {
        optionMap[s.dataset.optionPosition] = s.value;
      });
      var v = findVariant(this._variants, optionMap);
      if (!v) return;
      this.dataset.currentVariant = v.id;
      if (this._priceEl) this._priceEl.textContent = formatMoney(v.price);

      if (v.compare_at_price && v.compare_at_price > v.price) {
        if (this._compareEl) {
          this._compareEl.textContent = formatMoney(v.compare_at_price);
          this._compareEl.classList.remove('aqua-hidden');
        }
        if (this._discEl) {
          var pct = Math.round(((v.compare_at_price - v.price) * 100) / v.compare_at_price);
          this._discEl.textContent = '- ' + pct + '%';
          this._discEl.classList.remove('aqua-hidden');
        }
      } else {
        if (this._compareEl) { this._compareEl.textContent = ''; this._compareEl.classList.add('aqua-hidden'); }
        if (this._discEl) { this._discEl.textContent = ''; this._discEl.classList.add('aqua-hidden'); }
      }
    }

    getSelected() {
      if (!this._checkbox || !this._checkbox.checked) return null;
      return {
        id: parseInt(this.dataset.currentVariant, 10),
        quantity: parseInt(this._qtyInput.value, 10) || 1,
      };
    }
  }

  if (!customElements.get('aqua-accessory')) {
    customElements.define('aqua-accessory', AquaAccessory);
  }

  // ============================================================
  // <aqua-buy-bar>
  // ============================================================

  class AquaBuyBar extends HTMLElement {
    connectedCallback() {
      this._priceEl = this.querySelector('[data-buy-bar-price]');
      this._compareEl = this.querySelector('[data-buy-bar-compare]');
      this._discEl = this.querySelector('[data-buy-bar-discount]');
      this._cta = this.querySelector('[data-aqua-buy-cta]');
      this._ctaDirect = this.querySelector('[data-aqua-buy-cta-direct]');

      if (this._cta) {
        this._cta.addEventListener('click', () => this.submit());
      }
      if (this._ctaDirect) {
        // Direct add-to-cart: skip the HOVERCare modal entirely.
        this._ctaDirect.addEventListener('click', () => this.submitToCart());
      }
    }

    updatePrice(variant) {
      if (!variant) return;
      if (this._priceEl) this._priceEl.textContent = formatMoney(variant.price);
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        if (this._compareEl) {
          this._compareEl.textContent = formatMoney(variant.compare_at_price);
          this._compareEl.classList.remove('aqua-hidden');
        }
        if (this._discEl) {
          var pct = Math.round(((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price);
          this._discEl.textContent = '- ' + pct + '%';
          this._discEl.classList.remove('aqua-hidden');
        }
      } else {
        if (this._compareEl) { this._compareEl.textContent = ''; this._compareEl.classList.add('aqua-hidden'); }
        if (this._discEl) { this._discEl.textContent = ''; this._discEl.classList.add('aqua-hidden'); }
      }
      if (this._cta) this._cta.disabled = !variant.available;
      if (this._ctaDirect) this._ctaDirect.disabled = !variant.available;
    }

    submit() {
      // Mark this flow as "buy now" — final step redirects to checkout
      // instead of opening the cart drawer. The flag is preserved across the
      // HOVERCare modal interaction since the modal calls bar.submitToCart()
      // after the user picks Skip / Add.
      this._goToCheckout = true;

      // If the HOVERCare modal exists, intercept and open it first.
      var modal = document.querySelector('aqua-hovercare');
      if (modal && typeof modal.open === 'function') {
        modal.open();
        return;
      }
      this.submitToCart();
    }

    submitToCart(extraVariantId) {
      // Re-entrancy guard: ignore additional clicks while a request is in flight.
      if (this._submitting) return;

      // Snapshot + reset the "buy now" flag set by submit(). When true the
      // success path redirects to /checkout instead of opening the drawer.
      var goToCheckout = this._goToCheckout === true;
      this._goToCheckout = false;

      var picker = document.querySelector('aqua-variant-picker');
      if (!picker) return;
      var mainId = parseInt(picker.getCurrentVariantId(), 10);
      if (!mainId) return;

      var items = [{ id: mainId, quantity: 1 }];

      // Gather checked accessories
      document.querySelectorAll('aqua-accessory').forEach((acc) => {
        var sel = acc.getSelected();
        if (sel && sel.id) items.push(sel);
      });

      // Optional HOVERCare upsell
      if (extraVariantId) {
        items.push({ id: parseInt(extraVariantId, 10), quantity: 1 });
      }

      this._submitting = true;
      this.classList.add('is-loading');
      if (this._cta) this._cta.disabled = true;
      if (this._ctaDirect) this._ctaDirect.disabled = true;

      // Prefer the cart-drawer; fall back to cart-notification (alt Dawn config).
      var cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      var body = { items: items };
      if (cart && typeof cart.getSectionsToRender === 'function') {
        body.sections = cart.getSectionsToRender().map(function (s) { return s.id; }).join(',');
        body.sections_url = window.location.pathname;
        if (typeof cart.setActiveElement === 'function') {
          cart.setActiveElement(this._cta || document.activeElement);
        }
      }

      var self = this;
      fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(body),
      })
        .then(function (r) {
          return r.json().then(function (j) {
            if (!r.ok || j.status) throw new Error(j.description || j.message || 'Could not add to cart');
            return j;
          });
        })
        .then(function (res) {
          // "Buy Now" / Preorder path: skip the drawer and go straight to checkout.
          if (goToCheckout) {
            window.location.href = '/checkout';
            return;
          }

          // Notify Dawn's cart-items / quantity-input / etc. so they can rebind.
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            try {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'aqua-buy-bar',
                productVariantId: mainId,
                cartData: res,
              });
            } catch (_) {}
          }

          if (cart && typeof cart.renderContents === 'function' && res && res.sections) {
            try { cart.renderContents(res); } catch (_) {}
          } else if (cart && typeof cart.open === 'function') {
            // Sections weren't returned — fetch them manually then render.
            return fetch('/?sections=' + (body.sections || 'cart-drawer,cart-icon-bubble'))
              .then(function (r) { return r.json(); })
              .then(function (sections) {
                try { cart.renderContents({ sections: sections }); } catch (_) {}
              })
              .catch(function () { window.location.href = '/cart'; });
          } else {
            window.location.href = '/cart';
          }
        })
        .catch(function (err) {
          alert(err.message || 'Could not add to cart. Please try again.');
        })
        .then(function () {
          // Always reset state — runs after success OR failure.
          self.classList.remove('is-loading');
          if (self._cta) self._cta.disabled = false;
          if (self._ctaDirect) self._ctaDirect.disabled = false;
          self._submitting = false;
        });
    }
  }

  if (!customElements.get('aqua-buy-bar')) {
    customElements.define('aqua-buy-bar', AquaBuyBar);
  }

  // ============================================================
  // <aqua-hovercare>  — upsell modal between Preorder click and cart
  // ============================================================

  class AquaHovercare extends HTMLElement {
    connectedCallback() {
      this._radios = Array.from(this.querySelectorAll('input[type="radio"]'));
      this._cta = this.querySelector('[data-hovercare-add]');

      this.querySelectorAll('[data-hovercare-close]').forEach((el) => {
        el.addEventListener('click', () => this.close());
      });

      var skip = this.querySelector('[data-hovercare-skip]');
      if (skip) skip.addEventListener('click', () => this.handleSkip());

      if (this._cta) this._cta.addEventListener('click', () => this.handleAdd());

      this._radios.forEach((r) => {
        r.addEventListener('change', () => this.syncVisualState());
      });

      // Close on Escape
      this._onKey = (e) => {
        if (e.key === 'Escape' && !this.hidden) this.close();
      };
      document.addEventListener('keydown', this._onKey);
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this._onKey);
    }

    open() {
      // Portal to body so position:fixed escapes any ancestor stacking
      // context (transform / filter / contain on parent sections).
      if (this.parentElement !== document.body) {
        document.body.appendChild(this);
      }
      this.hidden = false;
      document.body.classList.add('aqua-hovercare-open');
    }

    close() {
      this.hidden = true;
      // Modal was dismissed without Skip/Add — clear the buy-bar's "buy now"
      // flag so a later Add-to-Cart click doesn't accidentally redirect to
      // checkout.
      var bar = document.querySelector('aqua-buy-bar');
      if (bar) bar._goToCheckout = false;
      document.body.classList.remove('aqua-hovercare-open');
      this.classList.remove('is-loading');
      if (this._cta) this._cta.disabled = false;
      // Re-enable the buy bar CTA so user can try again
      var barCta = document.querySelector('[data-aqua-buy-cta]');
      if (barCta) barCta.disabled = false;
      var bar = document.querySelector('aqua-buy-bar');
      if (bar) bar.classList.remove('is-loading');
    }

    syncVisualState() {
      this._radios.forEach((r) => {
        var card = r.closest('.aqua-hovercare-card');
        if (card) card.classList.toggle('is-active', r.checked);
      });
    }

    handleSkip() {
      this.close();
      var bar = document.querySelector('aqua-buy-bar');
      if (bar) bar.submitToCart();
    }

    handleAdd() {
      var checked = this._radios.find((r) => r.checked);
      var extraId = checked && checked.value ? parseInt(checked.value, 10) : null;
      this.classList.add('is-loading');
      if (this._cta) this._cta.disabled = true;
      // Hide modal but keep body locked while we submit
      this.hidden = true;
      var bar = document.querySelector('aqua-buy-bar');
      if (bar) bar.submitToCart(extraId);
    }
  }

  if (!customElements.get('aqua-hovercare')) {
    customElements.define('aqua-hovercare', AquaHovercare);
  }

  // ============================================================
  // Cross-component: emit country change for accessory sync
  // ============================================================
  document.addEventListener('change', function (e) {
    if (e.target && e.target.matches && e.target.matches('input[name^="aqua-country"]') && e.target.checked) {
      document.dispatchEvent(new CustomEvent('aqua:country-changed', {
        detail: { country: e.target.value },
      }));
    }
  });
})();